import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ExternalLink, DollarSign, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

const COLUMN_COLORS = {
  "New Lead": "bg-white/5",
  "Ready to Contact": "bg-blue-500/5",
  "Contacted": "bg-blue-500/5",
  "Follow-Up Needed": "bg-amber-500/5",
  "Audit Requested": "bg-purple-500/5",
  "Demo Sent": "bg-purple-500/5",
  "Interested": "bg-emerald-500/5",
  "Meeting Booked": "bg-emerald-500/5",
  "Proposal Sent": "bg-emerald-500/5",
  "Closed Won": "bg-emerald-500/10",
  "Closed Lost": "bg-red-500/5",
};

const LABEL_TAG = {
  "Hot Lead": "text-red-300",
  "Good Lead": "text-emerald-300",
  "Moderate Lead": "text-amber-300",
  "Low Priority": "text-white/40",
};

export default function Pipeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [moving, setMoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pipeline");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (leadId, toStatus, fromStatus) => {
    if (!leadId || toStatus === fromStatus) return;
    // Optimistic update
    setData((d) => {
      const next = { ...d, items_by_column: { ...d.items_by_column } };
      const from = next.items_by_column[fromStatus] || [];
      const idx = from.findIndex((x) => x.id === leadId);
      if (idx === -1) return d;
      const [item] = from.splice(idx, 1);
      next.items_by_column[fromStatus] = [...from];
      item.status = toStatus;
      next.items_by_column[toStatus] = [item, ...(next.items_by_column[toStatus] || [])];
      return next;
    });
    setMoving(true);
    try {
      await api.post("/admin/pipeline/move", { lead_id: leadId, to_status: toStatus });
      toast.success(`Moved to ${toStatus}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      load();
    } finally {
      setMoving(false);
    }
  };

  const onDragStart = (item, fromStatus) => (e) => {
    setDragging({ id: item.id, from: fromStatus });
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };
  const onDragOver = (col) => (e) => {
    e.preventDefault();
    if (dragOver !== col) setDragOver(col);
  };
  const onDrop = (col) => (e) => {
    e.preventDefault();
    if (dragging) move(dragging.id, col, dragging.from);
    setDragging(null);
    setDragOver(null);
  };

  if (loading || !data) {
    return <div className="text-white/50 text-sm"><Loader2 className="inline-block mr-2 animate-spin" size={14} /> Loading pipeline…</div>;
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div>
        <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
          Kanban
        </span>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Pipeline</h1>
        <p className="text-white/50 text-sm mt-1">
          Drag leads between columns to update their status. Changes save automatically.
        </p>
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {data.columns.map((col) => {
            const items = data.items_by_column[col] || [];
            const isOver = dragOver === col;
            return (
              <div
                key={col}
                data-testid={`column-${col.toLowerCase().replace(/\s+/g, "-")}`}
                onDragOver={onDragOver(col)}
                onDragLeave={() => setDragOver(null)}
                onDrop={onDrop(col)}
                className={`w-72 shrink-0 rounded-2xl border transition-colors ${
                  isOver ? "border-blue-500/60 bg-blue-500/5" : "border-white/10 " + (COLUMN_COLORS[col] || "bg-white/[0.02]")
                }`}
              >
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white truncate">{col}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-0.5">{items.length} lead{items.length === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <ul className="p-3 space-y-2 min-h-[120px]">
                  {items.map((it) => (
                    <li
                      key={it.id}
                      draggable
                      onDragStart={onDragStart(it, col)}
                      onDragEnd={onDragEnd}
                      data-testid={`card-${it.id}`}
                      className={`group card-soft rounded-lg p-3 cursor-grab active:cursor-grabbing ${
                        dragging?.id === it.id ? "opacity-40" : ""
                      }`}
                    >
                      <Link to={`/admin/dashboard/leads/${it.id}`} className="block">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm text-white font-medium truncate group-hover:text-blue-200 transition-colors">
                            {it.business_name || it.contact_name || "Untitled"}
                          </div>
                          <ExternalLink size={11} className="text-white/25 group-hover:text-white/60 shrink-0" />
                        </div>
                        <div className="mt-1 text-[11px] text-white/50 truncate">
                          {[it.niche, it.city].filter(Boolean).join(" · ") || "—"}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
                          {it.lead_score != null && (
                            <span className={`inline-flex items-center gap-1 ${LABEL_TAG[it.lead_label] || "text-white/50"}`}>
                              <Sparkles size={10} /> {it.lead_score}
                            </span>
                          )}
                          {it.estimated_deal_value != null && (
                            <span className="inline-flex items-center gap-1 text-white/50">
                              <DollarSign size={10} /> {Number(it.estimated_deal_value).toLocaleString()}
                            </span>
                          )}
                          {it.lead_source && (
                            <span className="text-white/35 truncate max-w-[110px]">{it.lead_source}</span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                  {items.length === 0 && (
                    <li className="text-xs text-white/25 text-center py-6">Drop leads here</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {moving && (
        <div className="text-xs text-white/50 inline-flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Saving…
        </div>
      )}
    </div>
  );
}
